import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bootstrapSession, syncProfilePreferences } from '@/auth/session';
import { useSession } from '@/auth/useSession';
import { Toaster } from '@/components/toast';
import { startSync, stopSync } from '@/sync/client';
import { dailyReconcile } from '@/features/events/effects';
import { router } from './router';
import { UpdatePrompt } from './UpdatePrompt';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false, networkMode: 'online' } },
});

let booted = false;

export function App() {
  const session = useSession();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (booted) return;
    booted = true;
    void bootstrapSession();
  }, []);

  // PL-12: switching language changes every string at once, without reload.
  const locale = session.profile?.locale ?? 'am';
  useEffect(() => {
    const retry = () => { void syncProfilePreferences(); };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);
  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }, [locale, i18n]);

  useEffect(() => {
    if (session.status !== 'signedIn') return;
    startSync();
    if (!navigator.onLine) void dailyReconcile(); // offline all day: still keep reminders current
    return () => stopSync();
  }, [session.status]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
      <UpdatePrompt />
    </QueryClientProvider>
  );
}
